import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowRight } from "lucide-react";

export default function FoodsPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState({ total: 0, items: [] });
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 40;

  useEffect(() => {
    api.get("/reference").then((r) => setRef(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/foods", {
          params: {
            q,
            category: category === "all" ? "" : category,
            limit: LIMIT,
            offset,
          },
        })
        .then((r) => setData(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, offset]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8 fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Food Library</h1>
        <p className="text-slate-500 mt-2">Browse and evaluate any food against your health profile.</p>
      </div>

      <Card className="rounded-2xl border-emerald-100 p-4 mb-6 bg-white fade-up delay-1">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search foods, ingredients or tags…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0); }}
              data-testid="food-search-input"
              className="pl-10 h-11 rounded-xl"
            />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setOffset(0); }}>
            <SelectTrigger className="md:w-56 h-11 rounded-xl" data-testid="food-category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {ref?.categories?.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="text-sm text-slate-500 mb-3">
        {loading ? "Searching…" : `${data.total} results`}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.items.map((f, i) => (
          <Link to={`/foods/${f.id}`} key={f.id} data-testid={`food-item-${f.id}`}>
            <Card className="rounded-2xl border-emerald-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all bg-white h-full">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="font-display font-semibold text-slate-900 leading-tight">{f.name}</div>
                <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
              </div>
              <div className="text-xs text-emerald-700 mb-2">{f.category}</div>
              <div className="flex gap-3 text-xs text-slate-500">
                <span>{f.kcal} kcal</span>
                <span>{f.protein_g}g protein</span>
                <span>{f.fiber_g}g fiber</span>
              </div>
              {f.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {f.tags.slice(0, 3).map((t) => (
                    <Badge key={t} variant="outline" className="rounded-full text-[10px] border-sky-200 text-sky-700 bg-sky-50">{t}</Badge>
                  ))}
                </div>
              )}
            </Card>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {data.total > LIMIT && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button variant="outline" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} className="rounded-full" data-testid="page-prev">
            Previous
          </Button>
          <span className="text-sm text-slate-500">
            {offset + 1}–{Math.min(offset + LIMIT, data.total)} of {data.total}
          </span>
          <Button variant="outline" disabled={offset + LIMIT >= data.total} onClick={() => setOffset((o) => o + LIMIT)} className="rounded-full" data-testid="page-next">
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
